import React, { useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Input,
  Chip,
  Spinner,
  Tabs,
  Tab,
} from '@heroui/react';
import {
  Plus,
  Pencil,
  Archive,
  Sparkle,
  List,
  Stack,
  ArchiveBoxIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { AssetCategoryForm, InlineQuickCreate } from 'components/assetCategories';
import { AssetInstancesTable } from 'components/assets';
import { useAssetCategories, useUpdateAssetCategory } from 'hooks/useAssets';
import { AssetCategory } from 'shared/types/asset';

const AssetCategoryManagementScreen: React.FC = () => {
  const { categories, isLoading, refetch } = useAssetCategories();
  const { updateCategory } = useUpdateAssetCategory();

  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AssetCategory | undefined>(undefined);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [activeTab, setActiveTab] = useState<string>('instances');

  const handleCreate = () => {
    setFormMode('create');
    setEditingCategory(undefined);
    setIsFormOpen(true);
  };

  const handleQuickSetup = () => {
    setShowQuickCreate(true);
  };

  const handleEdit = (category: AssetCategory) => {
    setFormMode('edit');
    setEditingCategory(category);
    setIsFormOpen(true);
  };

  const handleToggleActive = async (category: AssetCategory) => {
    const newStatus = !category.active;
    const confirmMessage = newStatus
      ? `Weet je zeker dat je "${category.name}" wilt activeren?`
      : `Weet je zeker dat je "${category.name}" wilt deactiveren? Inactieve categorieën zijn niet zichtbaar bij het aanmaken van voorzieningen.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await updateCategory(String(category.id), { active: newStatus });
      toast.success(
        newStatus ? 'Categorie succesvol geactiveerd' : 'Categorie succesvol gedeactiveerd'
      );
      refetch();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error(error instanceof Error ? error.message : 'Kan categorie niet bijwerken');
    }
  };

  const handleFormSuccess = () => {
    refetch();
    setShowQuickCreate(false);
  };

  // Filter and search logic
  const filteredCategories = categories.filter((category) => {
    const matchesSearch =
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (category.description &&
        category.description.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  // Sort by display order
  const sortedCategories = [...filteredCategories].sort((a, b) => a.displayOrder - b.displayOrder);

  // Empty state - no categories yet
  const isEmpty = categories.length === 0;

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Voorzieningen</h1>
        <p className="text-sm text-gray-600">
          Configureer categorieën, types en velden voor voorzieningen in je vastgoedportefeuille
        </p>
      </div>

      {/* Empty State - First Time Experience */}
      {isEmpty && !isLoading && (
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-dashed border-gray-300 mb-6">
            <CardBody className="p-8 text-center">
              <Stack size={48} weight="duotone" className="text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Beheer je voorzieningen
              </h2>
              <p className="text-gray-600 mb-6 max-w-xl mx-auto">
                Begin met het configureren van je eerste voorziening categorie. Je kunt kiezen uit
                vooraf ingestelde templates of helemaal opnieuw beginnen.
              </p>

              {/* Two-Path Entry */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <Card
                  isPressable
                  onPress={handleQuickSetup}
                  className="border-2 border-moss-200 hover:border-moss-primary hover:shadow-md transition-all"
                >
                  <CardBody className="p-6 text-center">
                    <Sparkle size={36} weight="fill" className="text-moss-primary mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Snelle Setup</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Gebruik een template en start in 2 minuten
                    </p>
                    <Chip size="sm" color="success" variant="flat">
                      Aanbevolen
                    </Chip>
                  </CardBody>
                </Card>

                <Card
                  isPressable
                  onPress={handleCreate}
                  className="border-2 border-gray-200 hover:border-gray-300 hover:shadow-md transition-all"
                >
                  <CardBody className="p-6 text-center">
                    <Stack size={36} weight="duotone" className="text-gray-500 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Aangepaste Categorie</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Configureer alles volledig naar wens
                    </p>
                    <Chip size="sm" color="default" variant="flat">
                      Geavanceerd
                    </Chip>
                  </CardBody>
                </Card>
              </div>
            </CardBody>
          </Card>

          {/* Quick Create Inline */}
          {showQuickCreate && (
            <InlineQuickCreate
              onSuccess={handleFormSuccess}
              onCancel={() => setShowQuickCreate(false)}
            />
          )}
        </div>
      )}

      {/* Main Content - Tabs */}
      {!isEmpty && !isLoading && (
        <Tabs
          selectedKey={activeTab}
          onSelectionChange={(key) => setActiveTab(key as string)}
          size="md"
          className="mb-4"
        >
          <Tab
            key="instances"
            title={
              <div className="flex items-center gap-2">
                <List size={18} weight="duotone" />
                <span>Alle Voorzieningen</span>
              </div>
            }
          >
            <div className="py-4">
              <Card className="border border-gray-200 mb-4">
                <CardBody className="p-4">
                  <div className="flex items-start gap-3">
                    <List size={24} weight="duotone" className="text-blue-600 flex-shrink-0" />
                    <div className="flex-1">
                      <h2 className="text-md font-bold text-gray-900 mb-1">
                        Overzicht Voorzieningen in Vastgoedportefeuille
                      </h2>
                      <p className="text-sm text-gray-600">
                        Alle voorzieningen die zijn toegewezen aan gebouwen, units en ruimtes. Klik
                        op de Entity ID om direct naar de locatie te navigeren.
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <AssetInstancesTable />
            </div>
          </Tab>

          <Tab
            key="categories"
            title={
              <div className="flex items-center gap-2">
                <Stack size={18} weight="duotone" />
                <span>Categorieën</span>
                <Chip size="sm" variant="flat" color="primary">
                  {categories.length}
                </Chip>
              </div>
            }
          >
            <div className="py-4">
              {/* Action Buttons Row */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Button
                    color="primary"
                    onPress={handleQuickSetup}
                    startContent={<Sparkle size={18} weight="fill" />}
                    size="md"
                  >
                    Snelle Setup
                  </Button>
                  <Button
                    color="default"
                    variant="bordered"
                    onPress={handleCreate}
                    startContent={<Plus size={18} weight="bold" />}
                    size="md"
                  >
                    Aangepaste Categorie
                  </Button>
                </div>
                <Input
                  placeholder="Zoek categorieën..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  startContent={<MagnifyingGlassIcon size={18} />}
                  className="max-w-xs"
                  size="sm"
                />
              </div>

              {/* Quick Create Inline */}
              {showQuickCreate && (
                <div className="mb-4">
                  <InlineQuickCreate
                    onSuccess={handleFormSuccess}
                    onCancel={() => setShowQuickCreate(false)}
                  />
                </div>
              )}

              {/* Categories Grid */}
              {sortedCategories.length === 0 && searchTerm && (
                <Card className="border-2 border-dashed border-gray-300">
                  <CardBody className="p-6 text-center">
                    <MagnifyingGlassIcon size={40} className="text-gray-400 mx-auto mb-3" />
                    <h3 className="text-md font-semibold text-gray-700 mb-1">
                      Geen categorieën gevonden
                    </h3>
                    <p className="text-sm text-gray-500">Probeer een andere zoekopdracht</p>
                  </CardBody>
                </Card>
              )}

              {sortedCategories.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {sortedCategories.map((category) => (
                    <Card
                      key={category.id}
                      className={`hover:shadow-md transition-all ${
                        !category.active
                          ? 'opacity-60 border-2 border-dashed border-gray-300'
                          : 'border border-gray-200'
                      }`}
                    >
                      <CardBody className="p-4">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {!category.active && (
                                <Chip size="sm" color="default" variant="flat">
                                  Inactief
                                </Chip>
                              )}
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1 truncate">
                              {category.name}
                            </h3>
                            {category.description && (
                              <p className="text-xs text-gray-600 line-clamp-2">
                                {category.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-2 mb-3 pt-3 border-t border-gray-100">
                          <div className="text-center p-2 bg-moss-50 rounded">
                            <p className="text-xl font-bold text-moss-primary">
                              {category.assetTypes?.length || 0}
                            </p>
                            <p className="text-xs text-gray-600">Types</p>
                          </div>
                          <div className="text-center p-2 bg-blue-50 rounded">
                            <p className="text-xl font-bold text-blue-600">
                              {category.fieldDefinitions?.length || 0}
                            </p>
                            <p className="text-xs text-gray-600">Velden</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="flat"
                            color="primary"
                            onPress={() => handleEdit(category)}
                            startContent={<Pencil size={14} />}
                            className="flex-1"
                          >
                            Bewerken
                          </Button>
                          <Button
                            size="sm"
                            variant="flat"
                            color={category.active ? 'warning' : 'success'}
                            onPress={() => handleToggleActive(category)}
                            isIconOnly
                          >
                            {category.active ? (
                              <ArchiveBoxIcon size={16} />
                            ) : (
                              <Archive size={16} />
                            )}
                          </Button>
                        </div>
                      </CardBody>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </Tab>
        </Tabs>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
          <span className="ml-3 text-gray-600">Categorieën laden...</span>
        </div>
      )}

      {/* Form Modal */}
      <AssetCategoryForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCategory(undefined);
        }}
        onSuccess={handleFormSuccess}
        category={editingCategory}
        mode={formMode}
      />
    </div>
  );
};

export default AssetCategoryManagementScreen;
